import { useState, useEffect, useCallback } from 'react';
import { 
  Upload, Image, Trash2, Search, X, Check, FolderPlus,
  Grid, List, ExternalLink, Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function SimpleMediaLibrary({ selectionMode = false, onSelect }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Fetch media
  const fetchMedia = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', '100');

      const res = await api.get(`/admin/cms/media?${params}`);
      setMedia(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch media:', err);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // Upload files
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        await api.post('/admin/cms/media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`Uploaded: ${file.name}`);
      } catch (err) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }

    setUploading(false);
    fetchMedia();
    e.target.value = '';
  };

  // Delete media
  const deleteMedia = async (id) => {
    if (!confirm('Delete this image?')) return;

    try {
      await api.delete('/admin/cms/media', { data: { ids: [id] } });
      toast.success('Image deleted');
      fetchMedia();
      if (selectedMedia?.id === id) {
        setSelectedMedia(null);
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Copy URL
  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  return (
    <div className={`${selectionMode ? '' : 'flex-1 bg-gray-50 dark:bg-gray-900'}`}>
      {/* Header */}
      {!selectionMode && (
        <div className="bg-white dark:bg-gray-800 border-b p-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Media Library</h1>
            <p className="text-sm text-muted-foreground">Upload and manage your images</p>
          </div>
          <div>
            <input
              type="file"
              id="media-upload"
              multiple
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
            <label htmlFor="media-upload">
              <Button asChild disabled={uploading}>
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Images'}
                </span>
              </Button>
            </label>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className={`${selectionMode ? '' : 'p-4'} flex items-center gap-4`}>
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search images..."
            className="pl-10"
          />
        </div>

        {/* View Toggle */}
        <div className="flex border rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800'}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800'}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>

        {/* Upload in selection mode */}
        {selectionMode && (
          <>
            <input
              type="file"
              id="media-upload-select"
              multiple
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
            <label htmlFor="media-upload-select">
              <Button variant="outline" size="sm" asChild disabled={uploading}>
                <span>
                  <Upload className="w-4 h-4 mr-1" />
                  Upload
                </span>
              </Button>
            </label>
          </>
        )}
      </div>

      {/* Content */}
      <div className={`${selectionMode ? 'mt-4' : 'p-4'}`}>
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : media.length === 0 ? (
          <div className="text-center py-12">
            <Image className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No images yet</h3>
            <p className="text-muted-foreground mb-4">Upload images to get started</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {media.map(item => (
              <div
                key={item.id}
                onClick={() => {
                  if (selectionMode) {
                    onSelect(item.url);
                  } else {
                    setSelectedMedia(item);
                  }
                }}
                className={`group relative aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                  selectedMedia?.id === item.id ? 'border-primary' : 'border-transparent hover:border-gray-300'
                }`}
              >
                <img
                  src={item.url}
                  alt={item.filename}
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {selectionMode ? (
                    <Button size="sm" variant="secondary">
                      <Check className="w-4 h-4 mr-1" />
                      Select
                    </Button>
                  ) : (
                    <>
                      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {media.map(item => (
              <div
                key={item.id}
                onClick={() => {
                  if (selectionMode) {
                    onSelect(item.url);
                  } else {
                    setSelectedMedia(item);
                  }
                }}
                className={`flex items-center gap-4 p-3 bg-white dark:bg-gray-800 rounded-lg border cursor-pointer hover:shadow-sm ${
                  selectedMedia?.id === item.id ? 'ring-2 ring-primary' : ''
                }`}
              >
                <img
                  src={item.url}
                  alt={item.filename}
                  className="w-16 h-16 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.filename}</p>
                  <p className="text-sm text-muted-foreground">
                    {item.type} • {formatFileSize(item.size)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {selectionMode ? (
                    <Button size="sm">Select</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Image Details */}
      {selectedMedia && !selectionMode && (
        <div className="fixed bottom-4 right-4 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border p-4">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-medium">Image Details</h3>
            <button onClick={() => setSelectedMedia(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <img
            src={selectedMedia.url}
            alt={selectedMedia.filename}
            className="w-full h-32 object-cover rounded-lg mb-3"
          />
          
          <div className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">Name:</span> {selectedMedia.filename}</p>
            <p><span className="text-muted-foreground">Size:</span> {formatFileSize(selectedMedia.size)}</p>
            {selectedMedia.dimensions && (
              <p><span className="text-muted-foreground">Dimensions:</span> {selectedMedia.dimensions.width} x {selectedMedia.dimensions.height}</p>
            )}
          </div>

          <div className="flex gap-2 mt-4">
            <Button size="sm" className="flex-1" onClick={() => copyUrl(selectedMedia.url)}>
              <Copy className="w-4 h-4 mr-1" />
              Copy URL
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={selectedMedia.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
